import ClearIcon from '@mui/icons-material/Clear';
import { FormControl, FormHelperText, Icon, InputLabel, MenuItem, SelectProps, Typography } from '@mui/material';
import { useField } from 'formik';
import { memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import ComponentLoader from '@/components/common/Loaders/ComponentLoader';
import FQSelect, { FQSELECT_VARIANTS } from '@/components/muiCustomized/FQSelect/FQSelect';
import { COLOR } from '@/globalStyles';

type AbsenceTypeSelectItem = {
    _id: string;
    name: string;
    color?: string;
    icon?: string;
    noLimits?: boolean;
    daysLeft?: number;
};

interface IAbsenceTypeSelectProps extends SelectProps {
    className?: string;
    absenceTypes?: AbsenceTypeSelectItem[];
    name?: string;
    isLoading?: boolean;
    showClearIcon?: boolean;
}

const AbsenceTypeSelect = ({
    className,
    absenceTypes,
    name = 'absenceType',
    isLoading,
    placeholder,
    showClearIcon,
    sx,
    ...props
}: IAbsenceTypeSelectProps) => {
    const [{ value: selectedAbsenceType, onBlur }, { error, touched }, { setValue }] = useField({
        name,
    });

    const onSelectChange = useCallback(
        (_, newValue) => {
            const newSelectedLimitUsage = absenceTypes?.find(({ _id }) => _id === newValue?.props?.value);
            setValue(newSelectedLimitUsage);
        },
        [absenceTypes, setValue]
    );

    const handleClearSelection = useCallback(() => {
        setValue('');
    }, []);

    const options = useMemo(() => createSelectOptions(absenceTypes), [absenceTypes]);

    return (
        <div className={className}>
            <ComponentLoader isLoading={isLoading} transparent>
                <FormControl fullWidth variant="outlined" size="small">
                    {props.label && <InputLabel>{props.label}</InputLabel>}
                    <FQSelect
                        name={name}
                        data-variant={FQSELECT_VARIANTS.fqOutlined}
                        value={selectedAbsenceType?._id || ''}
                        onChange={onSelectChange}
                        onBlur={onBlur}
                        error={touched && Boolean(error)}
                        endAdornment={
                            selectedAbsenceType && showClearIcon ? (
                                <Clear onClick={handleClearSelection} disabled={props.disabled} />
                            ) : undefined
                        }
                        sx={{
                            ...takePlaceholderStyles(placeholder),
                            ...sx,
                        }}
                        {...props}
                    >
                        {options}
                    </FQSelect>
                </FormControl>
                {touched && error ? <FormHelperText error>{error}</FormHelperText> : null}
            </ComponentLoader>
        </div>
    );
};
const createSelectOptions = (absenceTypes?: AbsenceTypeSelectItem[]) => {
    if (!absenceTypes) return null;
    return absenceTypes.map(({ _id, name, color, icon }) => (
        <MenuItem key={_id} value={_id}>
            <Icon data-testid={`${_id}-${icon}`} sx={{ color, marginRight: '0.5rem' }}>
                {icon}
            </Icon>
            <Typography variant="inherit" noWrap>
                {name}
            </Typography>
        </MenuItem>
    ));
};

const takePlaceholderStyles = placeholder => ({
    '& .MuiSelect-select .notranslate::after': placeholder
        ? {
              content: `"${placeholder}"`,
              opacity: 0.42,
          }
        : null,
});

const Clear = styled(ClearIcon)<{ disabled?: boolean }>`
    position: absolute;
    right: 30px;
    cursor: pointer;
    border-radius: 50%;
    color: ${COLOR.darkGray};
    padding: 3px;

    user-select: ${({ disabled }) => (disabled ? 'none' : 'unset')};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'unset')};
    :hover {
        background-color: ${COLOR.dirtyWhite};
    }
`;

const Memoized = memo(AbsenceTypeSelect);
const Styled = styled(Memoized)`
    .MuiSelect-select.MuiInputBase-input {
        display: flex;
        align-items: center;
    }
`;

export default Styled;
